import { useState } from 'react';
import {
  Mail,
  Phone,
  MessageSquare,
  MapPin,
  FileText,
  Volume2,
  ChevronDown,
  ChevronUp,
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  Send
} from 'lucide-react';
import type { Communication, CommunicationChannel } from '../types';

// WhatsApp icon (custom SVG)
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

interface CommunicationsTimelineProps {
  communications: Communication[];
  formatDate: (date: string) => string;
}

// Channel configuration with icons, colors, and labels
const channelConfig: Record<CommunicationChannel, {
  icon: React.ComponentType<{ className?: string }>;
  bgColor: string;
  textColor: string;
  borderColor: string;
  label: string;
}> = {
  email: {
    icon: Mail,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200',
    label: 'Email'
  },
  phone: {
    icon: Phone,
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    borderColor: 'border-green-200',
    label: 'Phone Call'
  },
  whatsapp: {
    icon: WhatsAppIcon,
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    label: 'WhatsApp'
  },
  sms: {
    icon: MessageSquare,
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200',
    label: 'SMS'
  },
  in_person: {
    icon: MapPin,
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-200',
    label: 'In-Person Meeting'
  },
  letter: {
    icon: FileText,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    label: 'Letter'
  },
  portal: {
    icon: Send,
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600',
    borderColor: 'border-indigo-200',
    label: 'Portal Message'
  },
  chat: {
    icon: MessageSquare,
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-600',
    borderColor: 'border-cyan-200',
    label: 'Live Chat'
  }
};

// Outcome labels
const outcomeLabels: Record<string, { label: string; color: string }> = {
  agreement_reached: { label: 'Agreement Reached', color: 'bg-green-100 text-green-700' },
  callback_scheduled: { label: 'Callback Scheduled', color: 'bg-blue-100 text-blue-700' },
  no_answer: { label: 'No Answer', color: 'bg-gray-100 text-gray-600' },
  wrong_number: { label: 'Wrong Number', color: 'bg-red-100 text-red-700' },
  refused_to_pay: { label: 'Refused', color: 'bg-red-100 text-red-700' },
  needs_time: { label: 'Needs Time', color: 'bg-yellow-100 text-yellow-700' },
  partial_info: { label: 'Partial Info', color: 'bg-purple-100 text-purple-700' },
  escalated: { label: 'Escalated', color: 'bg-orange-100 text-orange-700' }
};

// Format duration (seconds to readable)
const formatDuration = (seconds?: number): string => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hours}h ${remainMins}m`;
  }
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

export default function CommunicationsTimeline({ communications, formatDate }: CommunicationsTimelineProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<CommunicationChannel | 'all'>('all');

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Filter communications
  const filteredComms = channelFilter === 'all' 
    ? communications 
    : communications.filter(c => c.channel === channelFilter);

  // Get unique channels for filter
  const uniqueChannels = Array.from(new Set(communications.map(c => c.channel)));

  // Channel stats
  const channelStats = uniqueChannels.reduce((acc, channel) => {
    acc[channel] = communications.filter(c => c.channel === channel).length;
    return acc;
  }, {} as Record<string, number>);

  if (communications.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Communications</h3>
          <p className="text-gray-500">No communication history for this case yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with channel filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Communication History</h2>
          <span className="text-sm text-gray-500">{communications.length} interactions</span>
        </div>
        
        {/* Channel chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setChannelFilter('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              channelFilter === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({communications.length})
          </button>
          {uniqueChannels.map(channel => {
            const config = channelConfig[channel];
            const Icon = config.icon;
            return (
              <button
                key={channel}
                onClick={() => setChannelFilter(channel)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  channelFilter === channel
                    ? `${config.bgColor} ${config.textColor} ring-2 ring-offset-1 ring-${config.textColor.split('-')[1]}-400`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {config.label} ({channelStats[channel]})
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        <div className="space-y-4">
          {filteredComms.map((comm, index) => {
            const config = channelConfig[comm.channel] || channelConfig.email;
            const Icon = config.icon;
            const isExpanded = expandedId === comm.communication_id;
            const isInbound = comm.direction === 'inbound';

            return (
              <div key={comm.communication_id} className="relative pl-14">
                {/* Timeline dot with icon */}
                <div className={`absolute left-3 w-7 h-7 rounded-full ${config.bgColor} ${config.borderColor} border-2 flex items-center justify-center`}>
                  <Icon className={`w-3.5 h-3.5 ${config.textColor}`} />
                </div>

                {/* Card */}
                <div className={`bg-white rounded-xl shadow-sm border ${config.borderColor} overflow-hidden`}>
                  {/* Header - always visible */}
                  <button
                    onClick={() => toggleExpanded(comm.communication_id)}
                    className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Direction and channel badges */}
                        <div className="flex items-center gap-2 mb-1.5">
                          {isInbound ? (
                            <span className="flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                              <ArrowDownLeft className="w-3 h-3" /> Inbound
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                              <ArrowUpRight className="w-3 h-3" /> Outbound
                            </span>
                          )}
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bgColor} ${config.textColor}`}>
                            {config.label}
                          </span>
                          {comm.outcome && outcomeLabels[comm.outcome] && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${outcomeLabels[comm.outcome].color}`}>
                              {outcomeLabels[comm.outcome].label}
                            </span>
                          )}
                        </div>

                        {/* Subject/Title */}
                        <h3 className="font-medium text-gray-900 truncate">
                          {comm.subject || 'No subject'}
                        </h3>

                        {/* Summary */}
                        <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                          {comm.summary || comm.content.substring(0, 150)}
                        </p>
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(comm.sent_at)}
                        </div>
                        
                        {/* Duration for calls/meetings */}
                        {comm.duration_seconds && (
                          <span className="text-xs text-gray-500">
                            {formatDuration(comm.duration_seconds)}
                          </span>
                        )}

                        {/* Expand/collapse icon */}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                      {/* Audio player for calls/meetings */}
                      {comm.audio_url && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}>
                              <Volume2 className={`w-5 h-5 ${config.textColor}`} />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Recording Available</p>
                              <p className="text-xs text-gray-500">
                                Duration: {formatDuration(comm.duration_seconds)}
                              </p>
                            </div>
                            <audio controls className="h-10" preload="none">
                              <source src={comm.audio_url} type="audio/mpeg" />
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        </div>
                      )}

                      {/* Location for in-person meetings */}
                      {comm.location && (
                        <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{comm.location}</span>
                        </div>
                      )}

                      {/* Attendees for meetings */}
                      {comm.attendees && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Attendees</p>
                          <div className="flex flex-wrap gap-1">
                            {JSON.parse(comm.attendees).map((attendee: string, i: number) => (
                              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-sm">
                                {attendee}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Full content / transcript */}
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                          {(comm.channel === 'phone' || comm.channel === 'in_person') ? 'Transcript / Notes' : 'Full Content'}
                        </p>
                        <div className="bg-white rounded-lg border border-gray-200 p-3 max-h-80 overflow-y-auto">
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                            {comm.content}
                          </pre>
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-4">
                          <span>ID: {comm.communication_id}</span>
                          {comm.sent_by && <span>By: {comm.sent_by}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded ${
                            comm.status === 'delivered' || comm.status === 'completed' || comm.status === 'read'
                              ? 'bg-green-100 text-green-700'
                              : comm.status === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {comm.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
